import { AxiosResponse } from 'axios';
import { FormikErrors, FormikState } from 'formik';
import qs from 'qs';
import { Location } from 'react-router-dom';
// Cannot be moved to CRA alias
import { errorMessageIncognito } from '../constants/validation';
import { errorMessageInvalidMimeType } from '../constants/errors';
import { IError } from '../types';

type FormErrorData<T> = {
  submitted: boolean;
  touched: FormikState<T>['touched'];
  errors: FormikErrors<T>;
};

export const getStorageItem = (key: string, initialValue = '') => {
  try {
    const item = window.localStorage.getItem(key);

    return item ? JSON.parse(item) : initialValue;
  } catch (error) {
    throw new Error(errorMessageIncognito);
  }
};

export const putStorageItem = (key: string, value: Record<string, unknown> | string) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    throw new Error(errorMessageIncognito);
  }
};

export const removeStorageItem = (key: string) => {
  window.localStorage.removeItem(key);
};

export const empty = (value?: any) =>
  typeof value === 'undefined' ||
  value === null ||
  !value.toString().trim().length ||
  (value.constructor === Object && Object.keys(value).length === 0);

export const readQuery = (location?: Location) =>
  location?.search ? qs.parse(location.search, { ignoreQueryPrefix: true }) : null;

export const insertArrayItem = <T extends object>(data: Array<T>, index: number, newItem: T) => [
  ...data.slice(0, index),
  newItem,
  ...data.slice(index + 1),
];

export const removeObjectItem = <T extends object>(data: T, deleteKey: string) =>
  Object.entries(data).reduce((acc, [key, value]) => {
    if (key === deleteKey) return acc;

    return { ...acc, [key]: value };
  }, {});

export const capitalize = (string: string) => string.charAt(0).toUpperCase() + string.slice(1);

export const capitalizeWords = (sentence: string) => {
  const words = sentence.split(' ').map((word) => capitalize(word));
  return words.join(' ');
};

export const validExpression = (expression: string) => {
  const cleanExpression = expression.replace(
    /(["'])([^"']*?)\1/g,
    (match, quotes, content) => quotes + content.toLowerCase() + quotes
  );

  return cleanExpression.replace(
    /==\s*(1|true|0|false)\b/gi,
    (_, value) => ` == '${(value.toLowerCase() === 'true' || value === '1').toString()}'`
  );
};

export const mapMimeTypeToText = (mimeType: string): string => {
  if (mimeType !== '*' && !mimeType.includes('/')) throw new Error(errorMessageInvalidMimeType);

  if (mimeType === '*') return 'all file types';

  const [prefix, type] = mimeType.split('/');

  if (type === '*') return `any ${prefix}`;

  return type;
};

export const mapMimeTypesToText = (mimeTypes: string) =>
  `The following are accepted: ${
    mimeTypes.includes(',')
      ? mimeTypes
          .split(',')
          .reduce((acc, mimeType, i) => `${acc}${i !== 0 ? ', ' : ''}${mapMimeTypeToText(mimeType)}`, '')
      : mapMimeTypeToText(mimeTypes)
  }`;

export const valueLabelMap = (items: { label: string; value: string }[]): Record<string, string> =>
  items.reduce(
    (acc: Record<string, string>, { value, label }) => ({
      ...acc,
      [value]: label,
    }),
    {}
  );

export const labelValueMap = (items: { label: string; value: string }[]): Record<string, string> =>
  items.reduce(
    (acc: Record<string, string>, { value, label }) => ({
      ...acc,
      [label]: value,
    }),
    {}
  );

export const checkIfError = <T>(
  response: T | AxiosResponse<IError, IError>
): response is AxiosResponse<IError, IError> => {
  const errorResponse = response as AxiosResponse<IError, IError>;

  return !!errorResponse?.status && errorResponse?.statusText !== undefined;
};

export const checkFormError = <T>({ submitted, touched, errors }: FormErrorData<T>, key: keyof T) =>
  submitted || touched[key] ? errors[key] : undefined;

export const validateForm = <T>(data: FormErrorData<T>) =>
  Object.keys(data.errors).reduce((acc, key) => {
    const error = checkFormError<T>(data, key as keyof T);

    if (!error) return acc;

    return { ...acc, [key]: error };
  }, {} as Record<keyof T, string>);

export const formatErrors = (errors: Record<string, string>) =>
  Object.entries(errors).map(([key, val]) => `${capitalize(key).replace(/[A-Z]/g, (letter) => ` ${letter}`)} - ${val}`);

export const isJSON = (value: unknown): value is string => {
  try {
    JSON.parse(value as string);
  } catch (e) {
    return false;
  }
  return true;
};

export const matchString = (value: string, search: string) =>
  value.toLowerCase().trim().includes(search.toLowerCase().trim());

export const getUtcOffset = () => (new Date().getTimezoneOffset() / 60) * -1;
